'use client'

import type { Data } from '@repo/strapi'
import AppLink from '@/components/elementary/AppLink'
import CkEditorCSRRenderer from '@/components/elementary/CkEditorRenderer/CkEditorCSRRenderer'
import { Tag } from '@/components/elementary/Tag'
import { ArrowRight } from '@/components/icons/ArrowRight'
import Heading from '@/components/typography/Heading'
import { Paragraph } from '@/components/typography/Paragraph'
import { formatDate } from '@/lib/dates'
import { CHANGELOG_TEST_IDS } from '@/lib/test-ids'
import type { ServerRenderedEntry } from '@/types/helpers'

export const ChangelogItem = ({
	entryItem,
}: {
	entryItem: ServerRenderedEntry<Data.ContentType<'api::change-log.change-log'>>
}) => {
	const formattedDate = formatDate(entryItem?.changeDate ?? '', 'MMMM d, yyyy')

	return (
		<div
			className="flex flex-col gap-3 lg:flex-row lg:gap-12.5 lg:py-14 py-8 border-b border-neutral-light-500 last:border-b-0"
			data-test-id={CHANGELOG_TEST_IDS.ITEM}
		>
			<div className="relative flex justify-between items-center min-w-40 pt-1 lg:items-baseline">
				<Paragraph className="lg:sticky lg:top-33" fontSize="bodyRegularLarge">
					{formattedDate}
				</Paragraph>
				<div className="flex flex-wrap gap-2 lg:hidden">
					{entryItem.categories?.map((category) => (
						<Tag
							key={category.id}
							name={category?.name}
							customColors={{
								backgroundColor: category.backgroundColor?.color,
								borderColor: category.borderColor?.color,
							}}
						/>
					))}
				</div>
			</div>
			<div className="flex-1">
				<div className="hidden lg:flex lg:flex-wrap lg:gap-2 lg:mb-4">
					{entryItem.categories?.map((category) => (
						<Tag
							key={category.id}
							name={category?.name}
							customColors={{
								backgroundColor: category.backgroundColor?.color,
								borderColor: category.borderColor?.color,
							}}
						/>
					))}
				</div>

				{entryItem.title && (
					<Heading tag="h2" fontSize="displaySmall" className="mb-6 lg:mb-8">
						{entryItem.title}
					</Heading>
				)}

				{entryItem.serverProcessedHtmlContent ? (
					entryItem.serverProcessedHtmlContent
				) : (
					<CkEditorCSRRenderer htmlContent={entryItem.htmlContent} />
				)}

				{entryItem?.link?.href && (
					<AppLink
						href={entryItem?.link?.href}
						className="w-fit lok-text-body-bold-medium"
						iconEnd={<ArrowRight className="size-5.5" />}
					>
						{entryItem?.link?.label}
					</AppLink>
				)}
			</div>
		</div>
	)
}
